import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useInAppNotifications } from "../hooks/useInAppNotifications";
import "./NotificationBell.css";

const NotificationBell = () => {
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    requestNotificationPermission,
  } = useInAppNotifications();

  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showPermissionPrompt, setShowPermissionPrompt] = useState(false);
  const dropdownRef = useRef(null);
  const bellRef = useRef(null);

  // Check notification permission status
  useEffect(() => {
    if ("Notification" in window) {
      setShowPermissionPrompt(Notification.permission === "default" || Notification.permission === "denied");
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        bellRef.current &&
        !bellRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const past = new Date(dateString);
    const diffInSeconds = Math.floor((now - past) / 1000);

    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return past.toLocaleDateString();
  };

  const getNotificationIcon = (title) => {
    const lowerTitle = title?.toLowerCase() || "";
    if (lowerTitle.includes("payment") || lowerTitle.includes("transaction")) {
      return "💳";
    }
    if (lowerTitle.includes("user") || lowerTitle.includes("customer")) {
      return "👤";
    }
    if (lowerTitle.includes("complaint") || lowerTitle.includes("issue")) {
      return "⚠️";
    }
    if (lowerTitle.includes("success") || lowerTitle.includes("approved")) {
      return "✅";
    }
    if (lowerTitle.includes("version") || lowerTitle.includes("update")) {
      return "🔄";
    }
    return "📢";
  };

  const handleNotificationClick = (notification) => {
    // Mark as read
    markAsRead(notification.Id);
    
    // Close dropdown
    setOpen(false);
    
    // Navigate based on notification type
    // Type mapping: complaint -> /assistances, request -> /requests, 
    // payment -> /payment, customer -> /customers, promotion -> /promotions,
    // appversion/version -> /appversion
    const type = notification.Type?.toLowerCase();
    
    if (type === "complaint") {
      navigate("/assistances");
    } else if (type === "request") {
      navigate("/requests");
    } else if (type === "payment") {
      navigate("/payment");
    } else if (type === "customer") {
      navigate("/customers");
    } else if (type === "promotion") {
      navigate("/promotions");
    } else if (type === "appversion" || type === "version") {
      navigate("/appversion");
    } else {
      // Default navigation if type is not recognized
      navigate("/dashboard");
    }
  };

  const handleEnableNotifications = async () => {
    const granted = await requestNotificationPermission();
    if (granted) {
      setShowPermissionPrompt(false);
    }
  };

  return (
    <div className="notif-wrapper">
      <div 
        ref={bellRef}
        className={`notif-bell ${unreadCount > 0 ? 'has-unread' : ''}`} 
        onClick={() => setOpen(!open)}
      >
        <svg 
          className="bell-icon" 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
        </svg>
        {unreadCount > 0 && (
          <span className="notif-count">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </div>

      {open && (
        <div ref={dropdownRef} className="notif-dropdown">
          <div className="notif-header">
            <div className="notif-header-content">
              <h3>Notifications</h3>
              {unreadCount > 0 && (
                <span className="unread-badge">{unreadCount} new</span>
              )}
            </div>
            {unreadCount > 0 && (
              <button 
                className="notif-markall" 
                onClick={(e) => {
                  e.stopPropagation();
                  markAllAsRead();
                }}
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                Mark all read
              </button>
            )}
          </div>

          {showPermissionPrompt && (
            <div className="notif-permission-prompt">
              <div className="permission-content">
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                </svg>
                <span>Enable browser notifications to stay updated</span>
              </div>
              <button className="permission-btn" onClick={handleEnableNotifications}>
                Enable
              </button>
            </div>
          )}

          <div className="notif-list">
            {notifications.length === 0 ? (
              <div className="notif-empty">
                <svg 
                  className="empty-icon" 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                </svg>
                <p>No notifications yet</p>
                <span>You're all caught up!</span>
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.Id}
                  className={`notif-item ${n.IsRead ? "read" : "unread"}`}
                  onClick={() => handleNotificationClick(n)}
                >
                  <div className="notif-icon">{getNotificationIcon(n.Title)}</div>
                  <div className="notif-content">
                    <div className="notif-title">{n.Title}</div>
                    <div className="notif-message">{n.Message}</div>
                    <div className="notif-time">{getTimeAgo(n.CreatedAt)}</div>
                  </div>
                  {!n.IsRead && <div className="notif-dot"></div>}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
